/**
 * 統一プロンプト管理 hooks（テンプレート / プレセット）。
 * Mail / LINE NEWS の両系統が参照する単一データソース。
 * 仕様書: docs/01_spec/spec_common.md C-X 統一プロンプト管理
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  where,
} from 'firebase/firestore';

import { safeAddDoc, safeDeleteDoc, safeUpdateDoc } from '@/lib/safeWrite';

import type { PromptPreset, PromptScope, PromptTemplate } from '@goenhub/shared';

import { db } from '@/lib/firebase';

// ─────────────────────────────────────────────
// Templates
// ─────────────────────────────────────────────
export interface PromptTemplateRecord extends PromptTemplate {
  id: string;
}

/**
 * @param opts.scope 指定時はクライアント側で `scope === opts.scope || scope === 'both'` でフィルタ。
 */
export function usePromptTemplates(opts?: { scope?: PromptScope }) {
  const scope = opts?.scope;
  return useQuery({
    queryKey: ['prompt_templates', scope ?? 'all'],
    queryFn: async (): Promise<PromptTemplateRecord[]> => {
      const snap = await getDocs(query(collection(db, 'prompt_templates'), orderBy('name')));
      const all = snap.docs.map((d) => ({ id: d.id, ...(d.data() as PromptTemplate) }));
      if (!scope) return all;
      return all.filter((t) => t.scope === scope || t.scope === 'both');
    },
  });
}

export async function getPromptTemplate(id: string): Promise<PromptTemplateRecord | null> {
  const snap = await getDoc(doc(db, 'prompt_templates', id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...(snap.data() as PromptTemplate) };
}

export function useCreatePromptTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<PromptTemplate>) => {
      await safeAddDoc(collection(db, 'prompt_templates'), {
        ...data,
        scope: data.scope ?? 'both',
        isDefault: false,
        fields: data.fields ?? [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['prompt_templates'] }),
  });
}

export function useUpdatePromptTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; data: Partial<PromptTemplate> }) => {
      await safeUpdateDoc(doc(db, 'prompt_templates', args.id), {
        ...args.data,
        updatedAt: serverTimestamp(),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['prompt_templates'] }),
  });
}

export function useDeletePromptTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await safeDeleteDoc(doc(db, 'prompt_templates', id));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['prompt_templates'] }),
  });
}

// ─────────────────────────────────────────────
// Presets
// ─────────────────────────────────────────────
export interface PromptPresetRecord extends PromptPreset {
  id: string;
}

/**
 * @param opts.templateId 指定時は templateId で絞り込み（最優先）。
 * @param opts.scope      templateId 未指定時、scope で in クエリ（'both' も含む）。
 *
 * 注: 親 template の scope を変更しても preset.scope は自動更新されない。
 *     scope は preset 作成時に固定され、UI からは編集できない。
 */
export function usePromptPresets(opts?: { templateId?: string; scope?: PromptScope }) {
  const templateId = opts?.templateId;
  const scope = opts?.scope;
  return useQuery({
    queryKey: ['prompt_presets', templateId ?? null, scope ?? 'all'],
    queryFn: async (): Promise<PromptPresetRecord[]> => {
      let q;
      if (templateId) {
        q = query(
          collection(db, 'prompt_presets'),
          where('templateId', '==', templateId),
          orderBy('updatedAt', 'desc'),
        );
      } else if (scope) {
        q = query(
          collection(db, 'prompt_presets'),
          where('scope', 'in', [scope, 'both']),
          orderBy('updatedAt', 'desc'),
        );
      } else {
        q = query(collection(db, 'prompt_presets'), orderBy('updatedAt', 'desc'));
      }
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({ id: d.id, ...(d.data() as PromptPreset) }));
    },
  });
}

export function useCreatePromptPreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<PromptPreset>) => {
      await safeAddDoc(collection(db, 'prompt_presets'), {
        ...data,
        scope: data.scope ?? 'both',
        values: data.values ?? {},
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['prompt_presets'] }),
  });
}

export function useUpdatePromptPreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; data: Partial<PromptPreset> }) => {
      await safeUpdateDoc(doc(db, 'prompt_presets', args.id), {
        ...args.data,
        updatedAt: serverTimestamp(),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['prompt_presets'] }),
  });
}

export function useDeletePromptPreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await safeDeleteDoc(doc(db, 'prompt_presets', id));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['prompt_presets'] }),
  });
}
